import {
  Avatar,
  Chip,
  DateSpan,
  Dialog,
  GenericSuspense,
  IconButton,
  Table,
  useListFilterState,
  useListGraphqlQuery,
  useRuntimeEnv,
  useRuntimeEnvRolesIncludes,
  useTranslation,
  useTranslations,
  useTranslationRoute,
  useUrlParamsValueToString,
  useUserProfilePicture,
  Button,
} from "@harea/component";
import { TTimeOff } from "@harea/component";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { DayOffListQuery } from "../../../queries/DayOffListQuery";
import { useState, useMemo, useEffect } from "react";
import { useDayOffListSort } from "../../../hooks/useDayOffListSort";
import { DayOffListQuickFilter } from "../components/DayOffListQuickFilter";
import DayOffListFilter from "../components/DayOffListFilter";
import { VacationStatus } from "components/VacationStatus";
import { useDayOffDeleteMutation } from "../../../hooks/useDayOffDeleteMutation";
import ProjectContainer from "containers/ProjectContainer";
import { ProjectDayOffCalendarSection } from "../../calendar/container/ProjectDayOffViewsCalendarContainer";
import moment from "moment";

const ProjectDayOffViewsDemandContainer = () => {
  const [
    ADD,
    ADD_IN_BULK,
    MINE,
    TYPE,
    EMPLOYEE,
    DAYS,
    DEPARTMENT,
    CREATED_AT,
    STATUS,
    ACTIONS,
    CONFIRM,
    CANCEL,
    CONFIRMATION_TITLE,
    MESSAGE_CONFIRMATION,
    DEMAND,
    LIST,
    CALENDAR,
  ] = useTranslations(i18n);
  const t = useTranslation();
  const urlParamsToString = useUrlParamsValueToString();
  const sortItems = useDayOffListSort();
  const [openFilter, setOpenFilter] = useState(false);
  const { filter, setFilter } = useListFilterState(DEMAND_DAYOFF_LIST_ID);
  const { runtimeEnv } = useRuntimeEnv(true);
  const roleIncludes = useRuntimeEnvRolesIncludes(true);
  const isManager = roleIncludes("DASHBOARD_MANAGER");
  const isAdmin = roleIncludes("DASHBOARD_ADMIN_RH");
  const isSalarie = roleIncludes("DASHBOARD_SALARIE");
  const userDepartmentId = (runtimeEnv?.user as any)?.department?.id;
  const personnelDepartmentId = (runtimeEnv?.personnel as any)?.department?.id;
  const departmentId = userDepartmentId || personnelDepartmentId;
  const userPersonnelId = (runtimeEnv?.personnel as any)?.id;
  const isMyTimeoffs = window.location.pathname.includes("/my-timeoffs");
  const userProfilePicture = useUserProfilePicture();
  const tr = useTranslationRoute();
  const navigate = useNavigate();
  const urlParams = useUrlParamsValueToString();
  const mutation = useDayOffDeleteMutation();
  const { invalidateQuery } = useListGraphqlQuery(DEMAND_DAYOFF_LIST_ID);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");

  const defaultEmployeeFilter = useMemo(() => {
    // Salarié : le backend (TTimeOff.dataFilter) limite déjà la liste à ses congés.
    if (isSalarie) {
      return undefined;
    }
    if (isMyTimeoffs && userPersonnelId) {
      return { id: userPersonnelId };
    }
    if (isManager && departmentId) {
      return { department: { id: departmentId } };
    }
    return undefined;
  }, [isMyTimeoffs, isManager, isSalarie, userPersonnelId, departmentId]);

  const onlyMine = filter?.data?.employee?.id === userPersonnelId;

  const defaultFilterData = useMemo(() => {
    const filter: any = {};
    if (defaultEmployeeFilter) {
      filter.employee = defaultEmployeeFilter;
    }
    return filter;
  }, [defaultEmployeeFilter]);

  // Mêmes filtres que la liste (Table) pour la vue calendrier : les deux vues
  // affichent donc exactement les mêmes demandes.
  const calendarVariables = {
    data: {
      ...defaultFilterData,
      ...(filter?.data ?? {}),
    },
    includeAbsences: isMyTimeoffs,
    pageSize: 500,
    sort: [{ sort: "DESC", value: "createdAt" }],
  };

  useEffect(() => {
    if (isMyTimeoffs && userPersonnelId && !isSalarie) {
      setFilter((v: any) => ({
        ...v,
        data: {
          ...v?.data,
          employee: { id: userPersonnelId },
        },
      }));
    }
  }, [isMyTimeoffs, userPersonnelId, isSalarie, setFilter]);

  const resetFilter = () => {
    setFilter((v: any) => ({
      ...v,
      data: defaultEmployeeFilter ? { employee: defaultEmployeeFilter } : {},
    }));
  };

  const handleToggleOnlyMine = () => {
    setFilter((v: any) => ({
      ...v,
      data: {
        ...v.data,
        employee: onlyMine ? defaultEmployeeFilter : { id: userPersonnelId },
      },
    }));
  };

  const confirmDelete = () => {
    if (deleteTarget !== null) {
      mutation.mutate(
        { ids: [deleteTarget] },
        {
          onSuccess: () => {
            invalidateQuery();
            setDeleteTarget(null);
          },
        },
      );
    }
  };


  const canAddInBulk = useMemo(() => {
    return roleIncludes("TIMEOFF_ABSCENCE");
  }, [roleIncludes]);

  const creationPath = tr("admin/timeoffs/demands/create");

  const allColumns: any[] = [
      {
        key: "employee",
        sortKey: "employee.fullName",
        label: EMPLOYEE,
        value: (row: TTimeOff) => (
          <div className="flex items-center gap-3">
            <Avatar
              size="sm"
              src={userProfilePicture(row.employee?.id!)}
              fullName={row.employee?.fullName}
            />
            <Link
              to={tr(
                `admin/timeoffs/demands/${row.id}?${urlParams}`,
              )}
            >
              <span className="text-base font-semibold">
                {row.employee?.fullName}
              </span>
            </Link>
          </div>
        ),
      },
      {
        key: "type",
        sortKey: "type.designation",
        label: TYPE,
        value: (row: TTimeOff) => (
          <span className="font-semibold">
            {t(`Generic.de.${row.type?.designation}`)}
          </span>
        ),
      },
      {
        key: "status",
        sortKey: "status",
        label: STATUS,
        value: (row: TTimeOff) => <VacationStatus {...row} />,
      },
      {
        key: "days",
        sortKey: "countWorkingDays",
        label: DAYS,
        value: (row: TTimeOff) => (
          <div className="flex items-center gap-2">
            <Chip
              className="bg-default"
              label={`${row.countWorkingDays} ${t("std_days")}`}
            />
            <span className="text-xs text-muted-foreground">
              {moment(row.dateBegin).format("DD/MM")} -{" "}
              {moment(row.dateEnd).format("DD/MM")}
            </span>
          </div>
        ),
      },
      {
        key: "department",
        sortKey: "employee.department.designation",
        label: DEPARTMENT,
        value: (row: TTimeOff) => (
          <span>{row.employee?.department?.designation}</span>
        ),
      },
      {
        key: "createdAt",
        label: CREATED_AT,
        value: (row: TTimeOff) => <DateSpan value={row.createdAt} />,
      },
      {
        key: "actions",
        sortable: false,
        label: ACTIONS,
        value: (row: TTimeOff) => (
          <div className="flex gap-3 items-center">
            <IconButton
              icon="fa-solid fa-pen"
              onClick={() =>
                navigate(`${row.id}/edit?${urlParams}`)
              }
            />

            <IconButton
              icon="fa-solid fa-trash"
              onClick={() => setDeleteTarget(row.id as number)}
            />
          </div>
        ),
      },
    ];

  const columns = allColumns.filter((col) => {
    if (
      isMyTimeoffs &&
      (col.key === "employee" || col.key === "department")
    )
      return false;
    if (isManager && col.key === "department") return false;
    return true;
  });

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl">{DEMAND}</h2>
          <div className="flex gap-3 justify-end">
            {canAddInBulk && (
              <Button
                className="btn btn-primary"
                startIcon="fa-solid fa-plus"
                label={ADD_IN_BULK}
                onClick={() => {
                  const params = new URLSearchParams(urlParamsToString);
                  params.set("bulk", "true");
                  navigate(`${creationPath}?${params.toString()}`);
                }}
              />
            )}
              <Link to={`${creationPath}?${urlParamsToString}`}>
                <Button
                  className="btn btn-primary mr-4"
                  startIcon="fa-solid fa-plus"
                  label={ADD}
                />
              </Link>
          </div>
        </div>
        <div className="flex gap-4 items-center justify-between">
          <div className="flex gap-2 items-center">
            <Button
              className={`btn ${viewMode === "list" ? "btn-primary" : "btn-secondary"}`}
              startIcon="fa-solid fa-list"
              label={LIST}
              onClick={() => setViewMode("list")}
            />
            <Button
              className={`btn ${viewMode === "calendar" ? "btn-primary" : "btn-secondary"}`}
              startIcon="fa-solid fa-calendar"
              label={CALENDAR}
              onClick={() => setViewMode("calendar")}
            />
          </div>
          <div className="flex gap-4 items-center">
            {(isManager || isAdmin) && !isMyTimeoffs && (
              <Chip
                className={onlyMine ? "bg-primary text-white" : "bg-secondary"}
                startIcon={onlyMine ? "fa-check" : "fa-user"}
                label={MINE}
                onClick={handleToggleOnlyMine}
              />
            )}
            <DayOffListQuickFilter
              listId={DEMAND_DAYOFF_LIST_ID}
              departmentId={isManager ? departmentId : undefined}
              isMyTimeoffs={isMyTimeoffs}
            />
            <IconButton icon="fa fa-filter-circle-xmark" onClick={resetFilter} />
            <IconButton
              icon="fa fa-filter"
              className="text-primary"
              onClick={() => setOpenFilter(!openFilter)}
            />
          </div>
        </div>

        {viewMode === "list" ? (
          <Table
          BoxProps={{
            className: "box-transparent shadow-none",
          }}
          id={DEMAND_DAYOFF_LIST_ID}
          selection={false}
          search={true}
          pagination={true}
          sort={{ items: sortItems }}
          GQLRequest={DayOffListQuery}
          defaultFilter={{
            pageSize: 50,
            page: 0,
            sort: [{ sort: "DESC", value: "createdAt" }],
            data: defaultFilterData,
            includeAbsences: isMyTimeoffs,
          }}
          columns={columns}
        />
        ) : (
          <ProjectContainer
            id="DayOffDemandCalendarContainer"
            view="dayGridMonth"
          >
            <ProjectDayOffCalendarSection variables={calendarVariables} />
          </ProjectContainer>
        )}
      </div>

      <Dialog
        open={deleteTarget !== null}
        confirmation
        title={CONFIRMATION_TITLE}
        message={MESSAGE_CONFIRMATION}
        onConfirm={confirmDelete}
        btnOk={{ label: CONFIRM, isSubmit: mutation.isLoading }}
        btnCancel={{ label: CANCEL }}
        onClose={() => {
          setDeleteTarget(null);
        }}
      />

      <GenericSuspense>
        <Outlet />
      </GenericSuspense>

      <DayOffListFilter
        open={openFilter}
        onClose={() => setOpenFilter(false)}
      />
    </>
  );
};

export default ProjectDayOffViewsDemandContainer;

export const DEMAND_DAYOFF_LIST_ID = "dayoff-demand-list-id";

const i18n = [
  "std_add",
  "std_add_in_bulk",
  "BSMRH.Project.DayOff.Management.Mine.Label",
  "std_type",
  "std_employee",
  "std_days",
  "std_department",
  "std_created_at",
  "std_status",
  "std_actions",
  "std_confirm",
  "std_cancel",
  "std_confirmation",
  "BSMRH.DayOff.Leaving.Delete.Confirm.Message",
  "std_demand",
  "std_list",
  "std_calendar",
];
